import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../config/axios';
import API_CONFIG from '../config/api';

const UserProfilePage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, [navigate]);

  const generateVerificationLink = async (email) => {
    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.RESEND_VERIFICATION_CODE, {
        email: email
      });
      
      if (response.status === 200) {
        console.log('Verification link generated successfully');
      }
    } catch (error) {
      console.error('Failed to generate verification link:', error);
      // Don't throw error here - let user proceed to verification page
      // They can manually resend if needed
    }
  };

  const fetchUserData = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUTH.ME);
      
      if (response.status === 200) {
        const user = response.data;
        setUserData(user);
        setIsAuthorized(user.authorized);

        // Debug logging
        console.log('User data:', user);
        console.log('Paid:', user.paid);
        console.log('Email verified:', user.email_verified);

        // Convert to boolean to handle string values
        const isPaid = Boolean(user.paid);
        const isEmailVerified = Boolean(user.verified);

        console.log('Converted - Paid:', isPaid, 'Email verified:', isEmailVerified);

        // Implement routing logic based on user status
        if (!isEmailVerified) {
          // Email not verified - generate verification link and redirect
          console.log('Redirecting to verify-email - email not verified');
          await generateVerificationLink(user.email);
          navigate('/verify-email', { 
            replace: true,
            state: { email: user.email }
          });
          return;
        }
        // If email verified, stay on /me page
        console.log('Staying on /me page - email verified');
      }
    } catch (error) {
      console.error('User data fetch error:', error);
      if (error.response?.status === 401) {
        // User is not authenticated, show error message instead of redirecting
        setError('You are not logged in. Please sign in to view your profile.');
      } else if (error.response) {
        setError(error.response.data?.message || 'Failed to load user data. Please try again.');
      } else if (error.request) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);
      // Redirect to login after logout
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to login
      navigate('/login');
    }
  };

  const handleStartAssessment = () => {
    if (isAuthorized) {
      navigate('/page/1');
    }
  };

  if (isLoading) {
    return (
      <div className="questionnaire-page">
        <div className="page-header">
          <div className="page-info">
            <h2>Loading Profile...</h2>
            <p className="page-description">
              Please wait while we load your account information.
            </p>
          </div>
        </div>
        <div className="questions-container">
          <div className="question-block">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="success-icon">⏳</div>
              <p>Loading your profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const isAuthError = error.includes('not logged in');
    
    return (
      <div className="questionnaire-page">
        <div className="page-header">
          <div className="page-info">
            <h2>Your Profile</h2>
            <p className="page-description">
              {isAuthError ? 'You are not logged in.' : 'There was an issue loading your profile.'}
            </p>
          </div>
        </div>
        <div className="questions-container">
          <div className="question-block">
            <div className="simple-profile">
              <div className="profile-info">
                <h3>Guest User</h3>
                <div className="authorization-status not-authorized">
                  <span className="status-icon">❌</span>
                  <span>Not logged in</span>
                </div>
              </div>
              
              <div className="profile-actions">
                <button 
                  className="link-button"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="questionnaire-page">
      <div className="page-header">
        <div className="page-info">
          <h2>Your Profile</h2>
          <p className="page-description">
            Welcome back, {userData?.name}!
          </p>
        </div>
      </div>

      <div className="questions-container">
        <div className="question-block">
          <div className="simple-profile">
            <div className="profile-info">
              <h3>{userData?.name}</h3>
              <div className="user-details">
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{userData?.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{userData?.phone_number || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Organization ID:</span>
                  <span className="detail-value">{userData?.organization_id || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Organization Paid:</span>
                  <span className="detail-value">{userData?.organization_paid || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Organization Credits:</span>
                  <span className="detail-value">{userData?.organization_credits || 'N/A'}</span>
                </div>
              </div>
              {isAuthorized ? (
                <div className="authorization-status authorized">
                  <span className="status-icon">✅</span>
                  <span>Authorized for 3X Assessment</span>
                </div>
              ) : (
                <div className="authorization-status not-authorized">
                  <span className="status-icon">❌</span>
                  <span>Not authorized for 3X Assessment</span>
                </div>
              )}
            </div>
            
            <div className="profile-actions">
              <button 
                className="link-button"
                onClick={handleLogout}
              >
                Sign Out
              </button>
              {userData?.organization_id && (
                <button 
                  className="link-button"
                  onClick={() => navigate('/create-code')}
                >
                  Create One-Time Link
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
